import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { CATEGORIES, type Category } from "@/lib/categories";
import { SEVERITY_META, STATUS_META, priorityScore, type Severity } from "@/lib/reports";
import { ShieldAlert, Loader2, TrendingUp, MapPin, CheckCircle2, Flame, Layers, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { getHotspotAlerts, recomputeClusters, type HotspotAlert } from "@/lib/clusters.functions";

export const Route = createFileRoute("/intelligence")({
  head: () => ({
    meta: [
      { title: "Xamseen — Civic Intelligence" },
      { name: "description", content: "Aggregated civic intelligence: recurring problem hotspots, severity mix, priority queue and resolution performance." },
      { property: "og:title", content: "Xamseen — Civic Intelligence" },
      { property: "og:description", content: "Hotspots, priorities and resolution performance for community-reported problems." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: IntelligencePage,
});

type Row = {
  id: string;
  title: string;
  category: Category;
  status: string;
  severity: Severity | null;
  latitude: number | null;
  longitude: number | null;
  location_name: string | null;
  comment_count: number;
  created_at: string;
  updated_at: string;
  verify_count?: number;
};

type Tab = "overview" | "hotspots" | "queue";

function IntelligencePage() {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("overview");
  const [alerts, setAlerts] = useState<HotspotAlert[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  useEffect(() => {
    if (!user) { setAllowed(false); setLoading(false); return; }
    supabase.from("user_roles").select("role").eq("user_id", user.id).then(async ({ data }) => {
      const ok = (data ?? []).some((r: any) => r.role === "admin" || r.role === "moderator");
      setAllowed(ok);
      if (!ok) { setLoading(false); return; }

      const { data: posts } = await supabase
        .from("posts")
        .select("id, title, category, status, severity, latitude, longitude, location_name, comment_count, created_at, updated_at")
        .in("post_type", ["issue", "emergency"] as any)
        .order("created_at", { ascending: false })
        .limit(1000);

      const list = (posts ?? []) as any as Row[];
      if (list.length) {
        const { data: verifies } = await supabase
          .from("reactions")
          .select("post_id")
          .eq("reaction", "verify" as any)
          .in("post_id", list.map((r) => r.id));
        const map = new Map<string, number>();
        (verifies ?? []).forEach((v: any) => map.set(v.post_id, (map.get(v.post_id) ?? 0) + 1));
        list.forEach((r) => { r.verify_count = map.get(r.id) ?? 0; });
      }
      setRows(list);
      setLoading(false);
    });
  }, [user]);

  useEffect(() => {
    if (!allowed || tab !== "hotspots") return;
    setAlertsLoading(true);
    getHotspotAlerts().then((d) => { setAlerts(d); setAlertsLoading(false); });
  }, [allowed, tab]);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      await recomputeClusters();
      toast.success("Clusters and alerts refreshed");
      if (tab === "hotspots") {
        setAlertsLoading(true);
        getHotspotAlerts().then((d) => { setAlerts(d); setAlertsLoading(false); });
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Could not recompute");
    }
    setRecomputing(false);
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const resolved = rows.filter((r) => r.status === "resolved").length;
    const critical = rows.filter((r) => (r.severity ?? "info") === "critical" || (r.severity ?? "") === "urgent").length;
    const mapped = rows.filter((r) => r.latitude != null).length;
    const resolvedRows = rows.filter((r) => r.status === "resolved");
    const avgDays = resolvedRows.length
      ? resolvedRows.reduce((a, r) => a + (new Date(r.updated_at).getTime() - new Date(r.created_at).getTime()) / 86400000, 0) / resolvedRows.length
      : 0;
    return { total, resolved, critical, mapped, rate: total ? Math.round((resolved / total) * 100) : 0, avgDays };
  }, [rows]);

  const byCategory = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => m.set(r.category, (m.get(r.category) ?? 0) + 1));
    return [...m.entries()].map(([k, v]) => ({ key: k as Category, count: v })).sort((a, b) => b.count - a.count);
  }, [rows]);

  const bySeverity = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => m.set(r.severity ?? "info", (m.get(r.severity ?? "info") ?? 0) + 1));
    return (["critical", "urgent", "warning", "info"] as Severity[]).map((s) => ({ key: s, count: m.get(s) ?? 0 }));
  }, [rows]);

  const priority = useMemo(() => {
    return rows
      .filter((r) => r.status !== "resolved" && r.status !== "closed")
      .map((r) => ({ ...r, score: priorityScore(r) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
  }, [rows]);

  // Hotspots: ~1.1km grid buckets of recurring problems
  const hotspots = useMemo(() => {
    const m = new Map<string, { key: string; label: string; count: number; categories: Map<string, number> }>();
    rows.forEach((r) => {
      if (r.latitude == null || r.longitude == null) return;
      const key = `${r.latitude.toFixed(2)},${r.longitude.toFixed(2)}`;
      const entry = m.get(key) ?? { key, label: r.location_name ?? key, count: 0, categories: new Map() };
      entry.count += 1;
      entry.categories.set(r.category, (entry.categories.get(r.category) ?? 0) + 1);
      if (r.location_name && entry.label === key) entry.label = r.location_name;
      m.set(key, entry);
    });
    return [...m.values()].filter((h) => h.count >= 2).sort((a, b) => b.count - a.count).slice(0, 8);
  }, [rows]);

  if (loading) return <AppShell><div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin" /></div></AppShell>;

  if (!allowed) {
    return (
      <AppShell>
        <Card className="mx-auto max-w-md p-8 text-center">
          <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="font-medium">Authorized access only</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Civic Intelligence aggregates report data for city officials, agencies and platform moderators.
          </p>
          <Link to="/dashboard" className="mt-4 inline-flex text-sm font-medium text-primary hover:underline">See public community stats →</Link>
        </Card>
      </AppShell>
    );
  }

  const kpis = [
    { label: "Total reports", value: stats.total.toLocaleString(), icon: Layers },
    { label: "Resolved", value: `${stats.resolved} (${stats.rate}%)`, icon: CheckCircle2 },
    { label: "High / critical", value: stats.critical.toLocaleString(), icon: Flame },
    { label: "Mapped", value: stats.mapped.toLocaleString(), icon: MapPin },
    { label: "Avg. resolution", value: stats.avgDays ? `${stats.avgDays.toFixed(1)} days` : "—", icon: TrendingUp },
  ];

  return (
    <AppShell>
      <div className="space-y-6">
        <header>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-display text-2xl font-semibold tracking-tight">Civic Intelligence</h1>
              <p className="text-sm text-muted-foreground">Aggregated, anonymised report data — hotspots, severity mix, priority queue and resolution performance.</p>
            </div>
            <Button size="sm" variant="outline" disabled={recomputing} onClick={handleRecompute}>
              {recomputing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}Refresh clusters
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {kpis.map((k) => (
            <Card key={k.label} className="p-4">
              <k.icon className="mb-2 h-5 w-5 text-primary" />
              <div className="text-xl font-semibold">{k.value}</div>
              <div className="text-xs text-muted-foreground">{k.label}</div>
            </Card>
          ))}
        </div>

        <div className="border-b border-border">
          <div className="grid grid-cols-3">
            <TabButton active={tab === "overview"} onClick={() => setTab("overview")}>Overview</TabButton>
            <TabButton active={tab === "hotspots"} onClick={() => setTab("hotspots")}>Hotspots</TabButton>
            <TabButton active={tab === "queue"} onClick={() => setTab("queue")}>Priority queue</TabButton>
          </div>
        </div>

        {tab === "overview" && (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <h2 className="mb-4 font-display text-base font-semibold">Reports by category</h2>
              <div className="space-y-3">
                {byCategory.length === 0 && <p className="text-sm text-muted-foreground">No reports yet.</p>}
                {byCategory.map((row) => {
                  const max = Math.max(...byCategory.map((r) => r.count));
                  const meta = CATEGORIES[row.key];
                  return (
                    <div key={row.key}>
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5"><meta.icon className="h-3.5 w-3.5" style={{ color: meta.color }} />{meta.label}</span>
                        <span className="text-muted-foreground">{row.count}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full" style={{ width: `${(row.count / max) * 100}%`, background: meta.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="mb-4 font-display text-base font-semibold">Severity mix</h2>
              <div className="space-y-3">
                {bySeverity.map((row) => {
                  const max = Math.max(1, ...bySeverity.map((r) => r.count));
                  const meta = SEVERITY_META[row.key];
                  return (
                    <div key={row.key}>
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span>{meta.label}</span>
                        <span className="text-muted-foreground">{row.count}</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full" style={{ width: `${(row.count / max) * 100}%`, background: meta.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <h3 className="mb-3 mt-6 font-display text-base font-semibold">Recurring hotspots</h3>
              {hotspots.length === 0 ? (
                <p className="text-sm text-muted-foreground">No location clusters yet — hotspots appear when several reports share an area.</p>
              ) : (
                <ul className="space-y-2">
                  {hotspots.map((h) => {
                    const topCat = [...h.categories.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] as Category;
                    return (
                      <li key={h.key} className="flex items-center justify-between gap-3 rounded-xl border border-border p-2.5 text-sm">
                        <span className="min-w-0 truncate">
                          <MapPin className="mr-1.5 inline h-3.5 w-3.5 text-muted-foreground" />{h.label}
                        </span>
                        <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                          {topCat && <Badge variant="secondary" className="font-normal">{CATEGORIES[topCat].label}</Badge>}
                          {h.count} reports
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </div>
        )}

        {tab === "hotspots" && (
          <Card className="p-5">
            <h2 className="mb-1 font-display text-base font-semibold">Active hotspot alerts</h2>
            <p className="mb-4 text-xs text-muted-foreground">Generated from clusters with 3+ reports, urgent/critical severity, or a rising 14-day trend.</p>
            {alertsLoading ? (
              <div className="flex items-center gap-2 py-8 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading alerts…</div>
            ) : alerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No active hotspot alerts right now.</p>
            ) : (
              <ul className="space-y-3">
                {alerts.map((a) => {
                  const sev = SEVERITY_META[(a.severity ?? "warning") as Severity];
                  return (
                    <li key={a.id} className={cn(
                      "flex items-start gap-3 rounded-xl border p-3",
                      a.severity === "critical" || a.severity === "urgent" ? "border-destructive/30 bg-destructive/5" : "border-warning/30 bg-warning/5"
                    )}>
                      <AlertTriangle className={cn("mt-0.5 h-4 w-4 shrink-0", a.severity === "critical" || a.severity === "urgent" ? "text-destructive" : "text-warning")} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold">{a.title}</span>
                          <Badge variant="outline" className={cn("font-normal", sev.badge)}>{sev.label}</Badge>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{a.description}</p>
                        {a.location_name && <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{a.location_name}</p>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        )}

        {tab === "queue" && (
          <Card className="p-5">
            <h2 className="mb-1 font-display text-base font-semibold">Priority queue</h2>
            <p className="mb-4 text-xs text-muted-foreground">Ranked by severity × community confirmations × recency.</p>
            {priority.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing outstanding.</p>
            ) : (
              <ol className="space-y-2">
                {priority.map((p, i) => {
                  const sev = SEVERITY_META[(p.severity ?? "info") as Severity];
                  return (
                    <li key={p.id}>
                      <Link to="/posts/$id" params={{ id: p.id }}
                        className="flex items-start gap-3 rounded-xl border border-border p-3 transition-colors hover:border-primary/40">
                        <span className="mt-0.5 w-5 shrink-0 text-sm font-semibold text-muted-foreground tabular-nums">{i + 1}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{p.title}</span>
                          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                            <Badge variant="outline" className={cn("font-normal", sev.badge)}>{sev.label}</Badge>
                            <span>{CATEGORIES[p.category].label}</span>
                            <span>· {STATUS_META[p.status]?.label ?? p.status}</span>
                            <span>· {p.verify_count ?? 0} confirmations</span>
                            {p.location_name && <span className="truncate">· {p.location_name}</span>}
                          </span>
                        </span>
                        <span className="shrink-0 text-sm font-semibold tabular-nums text-primary">{p.score}</span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            )}
          </Card>
        )}
      </div>
    </AppShell>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex h-12 items-center justify-center text-[15px] font-medium transition-colors hover:bg-accent/40",
        active ? "text-foreground" : "text-muted-foreground"
      )}>
      <span className="relative py-3">
        {children}
        {active && <span className="absolute inset-x-0 -bottom-px h-1 rounded-full bg-primary" />}
      </span>
    </button>
  );
}
